import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { Typography } from "@material-ui/core";
import {
	Page,
	PageContent,
	ScrollToTopOnMount,
	TitleWithHelperText,
} from "frame/components";
import { useRouter, isNotNullOrEmpty } from "utils";
import InductionMinutesCard from "../cards/induction-minutes-card";
import {
	AppendInductionMinutesForm,
	EditInductionMinutesForm,
} from "features/health-safety-forms";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
	root: {
		position: "relative",
		backgroundColor: theme.palette.white,
	},
	list: {
		margin: theme.spacing(3, 0),
	},
	heading: {
		marginTop: theme.spacing(4),
		[theme.breakpoints.down("xs")]: {
			marginTop: theme.spacing(0),
		},
	},
	empty: {
		marginTop: theme.spacing(3),
	},
	helperText: {
		position: "absolute",
		right: 20,
		marginTop: theme.spacing(-3),
	},
	helperTextIcon: {
		color: theme.palette.success.main,
	},
	embeddedRoot: {
		maxHeight: "min(70vh, 720px)",
		overflowY: "auto",
	},
	embeddedContent: {
		paddingTop: theme.spacing(1),
	},
}));

const InductionMinutesSiteList = ({
	role,
	onDelete,
	variant = "page",
	siteId: siteIdProp,
}) => {
	const classes = useStyles();
	const { match } = useRouter();
	const siteId = siteIdProp || match?.params?.siteId;
	const isEmbedded = variant === "embedded";

	const [editId, setEditId] = useState();
	const [appendId, setAppendId] = useState();
	const [viewId, setViewId] = useState(null);
	const [siteInductions, setSiteInductions] = useState(null);

	const inductionList = useSelector(
		({ healthSafety }) => healthSafety.inductions?.results
	);
	useEffect(() => {
		setSiteInductions(
			inductionList && siteId
				? inductionList.filter((node) => node.siteId === siteId)
				: []
		);
	}, [inductionList, siteId]);

	const empty = siteInductions && siteInductions.length <= 0;

	const onEdit = (id) => () => setEditId(id);
	const onAppend = (id) => () => setAppendId(id);
	const onView = (id) => () => setViewId(id);

	const onCancel = () => {
		setEditId(null);
		setAppendId(null);
		setViewId(null);
	};

	const modals = (
		<>
			{editId && (
				<EditInductionMinutesForm id={editId} onClose={onCancel} />
			)}
			{appendId && (
				<AppendInductionMinutesForm id={appendId} onClose={onCancel} />
			)}
			{viewId && (
				<EditInductionMinutesForm
					readOnly
					id={viewId}
					onClose={onCancel}
				/>
			)}
		</>
	);

	const body = (
		<PageContent pad className={isEmbedded ? classes.embeddedContent : undefined}>
			{!isEmbedded && (
				<Typography variant="h2" className={classes.heading}>
					Induction Meeting
				</Typography>
			)}

			{!empty && (
				<div className={classes.list}>
					{!isEmbedded && (
						<TitleWithHelperText
							className={classes.helperText}
							helperText="Attendance confirmed"
							icon={
								<CheckCircleIcon
									className={classes.helperTextIcon}
								/>
							}
						/>
					)}

					{isNotNullOrEmpty(siteInductions) &&
						siteInductions.map((node, index) => (
							<InductionMinutesCard
								key={`induction-listing-${index}-${node.id}`}
								inductionMinutes={node}
								onAppend={onAppend(node.id)}
								onEdit={onEdit(node.id)}
								onView={onView(node.id)}
								onDelete={onDelete}
								role={role}
							/>
						))}
				</div>
			)}

			{empty && (
				<Typography className={classes.empty} variant="body1">
					There are currently no Induction Meetings listed.
				</Typography>
			)}
		</PageContent>
	);

	if (isEmbedded) {
		return (
			<div className={classes.embeddedRoot}>
				{body}
				{modals}
			</div>
		);
	}

	return (
		<Page className={classes.root} title="Induction Meeting">
			<ScrollToTopOnMount />
			{body}
			{modals}
		</Page>
	);
};

InductionMinutesSiteList.propTypes = {
	role: PropTypes.string.isRequired,
	onDelete: PropTypes.func.isRequired,
	variant: PropTypes.oneOf(["page", "embedded"]),
	siteId: PropTypes.string,
};

export default InductionMinutesSiteList;
